// Переходы между экранами и листами по уровням навигации ux/02 (L0–L3, листы, гостевые листы).
// Вглубь — новый экран выезжает справа, прежний уходит влево и гаснет под ним; назад — наоборот, прежний поверх;
// смена вкладки (L0/L1) — перекрёстное проявление; замена на том же уровне (бой → итог) — проявление с приближением.
// Лист: закрытие — уезжает вниз, затемнение тает; смена листа на лист — затемнение не мигает, новое содержимое
// поднимается. Уходящее доигрывает «призрак» (ui/motion.ghost) — копия без тестовых меток и без нажатий.
import { useLayoutEffect, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';
import { animate, DUR, EASE, enter, ghost, reduced } from './motion.js';

interface Snap { el: HTMLElement; top: number; left: number; width: number; height: number }
export interface NavPos { key: string; level: number; depth: number; tab: string }

const isChrome = (el: Element) => el.classList.contains('action-bar') || el.classList.contains('nav-bar') || el.classList.contains('m-ghost');

function snap(root: Element | null, pick: (el: Element) => boolean): Snap[] {
  if (!root) return [];
  return [...root.children].filter(pick).map((el) => {
    const h = el as HTMLElement;
    return { el: h, top: h.offsetTop, left: h.offsetLeft, width: h.offsetWidth, height: h.offsetHeight };
  });
}

/** Экран внутри .shell: направление — по уровню и глубине стека, вкладка — отдельно. */
export function useScreenTransition(shell: RefObject<HTMLElement>, pos: NavPos): void {
  const prev = useRef<{ pos: NavPos; nodes: Snap[] } | null>(null);
  useLayoutEffect(() => {
    const root = shell.current;
    const was = prev.current;
    if (root && was && was.pos.key !== pos.key) {
      const fresh = [...root.children].filter((el) => !isChrome(el));
      const gone = was.nodes.filter((n) => !n.el.isConnected);
      const kind = was.pos.tab !== pos.tab ? 'tab'
        : pos.depth > was.pos.depth || pos.level > was.pos.level ? 'forward'
          : pos.depth < was.pos.depth || pos.level < was.pos.level ? 'back' : 'replace';
      const rm = reduced();
      for (const n of gone) {
        const rect = { top: n.top, left: n.left, width: n.width, height: n.height };
        if (kind === 'back') ghost(n.el, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(36px)' }], { rect, parent: root, above: true, duration: DUR.base });
        else if (kind === 'forward') ghost(n.el, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(-24px)', offset: 0.7 }, { opacity: 0, transform: 'translateX(-24px)' }], { rect, parent: root, duration: DUR.fast });
        else ghost(n.el, [{ opacity: 1 }, { opacity: 0 }], { rect, parent: root, duration: DUR.fast });
      }
      if (!rm || kind === 'tab' || kind === 'replace') {
        for (const el of fresh) {
          enter(el, kind === 'forward' ? 'fromRight' : kind === 'back' ? 'fromLeft' : kind === 'replace' ? 'zoom' : 'fade', {
            duration: kind === 'tab' ? DUR.fast + 40 : DUR.base,
            easing: EASE.out,
          });
        }
      }
    }
    prev.current = { pos, nodes: snap(root, (el) => !isChrome(el)) };
  });
}

/** Листы и окна — дети #ui с классом overlay. Закрытие доигрывается призраком, смена листа не мигает затемнением. */
export function useSheetTransition(host: RefObject<HTMLElement | null>, sig: string): void {
  const prev = useRef<{ sig: string; nodes: Snap[] }>({ sig: '', nodes: [] });
  useLayoutEffect(() => {
    const root = host.current;
    const was = prev.current;
    if (root && was.sig !== sig) {
      const gone = was.nodes.filter((n) => !n.el.isConnected);
      const fresh = [...root.children].filter((el) => el.classList.contains('overlay') && !el.classList.contains('m-ghost') && !was.nodes.some((n) => n.el === el)) as HTMLElement[];
      for (const n of gone) ghostOverlay(n.el, root, fresh.length > 0);
      // Лист сменился листом: затемнение уже стоит — не проявлять его заново; содержимое — коротким подъёмом.
      if (gone.length && fresh.length) {
        for (const el of fresh) {
          for (const a of el.getAnimations?.() ?? []) a.finish();
          const panel = el.querySelector<HTMLElement>(':scope > .sheet, :scope > .dialog');
          if (panel) { for (const a of panel.getAnimations?.() ?? []) a.cancel(); enter(panel, 'rise', { duration: DUR.base }); }
        }
      }
    }
    prev.current = { sig, nodes: snap(root, (el) => el.classList.contains('overlay') && !el.classList.contains('m-ghost')) };
  });
}

/** Призрак закрытого листа: затемнение тает, панель уезжает вниз (окно по центру — сжимается и гаснет). */
function ghostOverlay(el: HTMLElement, root: HTMLElement, replaced: boolean): void {
  const dur = DUR.fast + 40;
  const copy = ghost(el, [{ opacity: 1 }, { opacity: 0 }], { parent: root, above: true, duration: replaced ? DUR.fast : dur, easing: EASE.in });
  const panel = copy?.querySelector<HTMLElement>(':scope > .sheet, :scope > .dialog');
  if (!panel || replaced || reduced()) return;
  animate(panel, panel.classList.contains('dialog')
    ? [{ transform: 'none' }, { transform: 'scale(.94)' }]
    : [{ transform: 'none' }, { transform: 'translateY(60%)' }], { duration: dur, easing: EASE.in, fill: 'forwards' });
}
